import { useEffect, useMemo, useState } from 'react';
import { Save } from '@/components/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { FormGroup } from '@/components/form/FormGroup';
import { Input } from '@/components/form/Input';
import { Select } from '@/components/form/Select';
import { Textarea } from '@/components/form/Textarea';
import { ItemsTable } from '@/components/form/ItemsTable';
import { useToast } from '@/components/ui/useToast';
import { createPo, updatePo, type Po, type PoItem } from '@/api/po';
import { listParties, type Party } from '@/api/parties';
import { listItems } from '@/api/items';
import { CategoryItemPicker } from '@/components/form/CategoryItemPicker';
import { ItemThumb } from '@/components/ui/ItemThumb';
import { apiError } from '@/lib/apiError';
import { useFieldErrors } from '@/lib/useFieldErrors';
import { formatINR, todayISO } from '@/lib/format';

interface Props {
  open: boolean;
  editing: Po | null;
  onClose: () => void;
}

type FormState = {
  poDate: string;
  subject: string;
  vendorId: number | '';
  deliveryDate: string;
  paymentTermDays: string;
  deliveryAddress: string;
  termsConditions: string;
  items: PoItem[];
};

function emptyForm(): FormState {
  return {
    poDate: todayISO(),
    subject: '',
    vendorId: '',
    deliveryDate: '',
    paymentTermDays: '30',
    deliveryAddress: '',
    termsConditions: 'Standard terms apply.',
    items: [{ itemName: '', hsn: '', qty: '', unit: '', unitPrice: '', gstPct: '12' }],
  };
}

function fromPo(p: Po): FormState {
  return {
    poDate: p.poDate.slice(0, 10),
    subject: p.subject ?? '',
    vendorId: p.vendorId,
    deliveryDate: p.deliveryDate ? p.deliveryDate.slice(0, 10) : '',
    paymentTermDays: String(p.paymentTermDays),
    deliveryAddress: p.deliveryAddress ?? '',
    termsConditions: p.termsConditions ?? '',
    items: p.items.length
      ? p.items.map((it) => ({
          itemId: it.itemId,
          itemName: it.itemName,
          hsn: it.hsn ?? '',
          qty: it.qty,
          unit: it.unit,
          unitPrice: it.unitPrice,
          gstPct: it.gstPct,
        }))
      : [{ itemName: '', hsn: '', qty: '', unit: '', unitPrice: '', gstPct: '12' }],
  };
}

export function PoFormModal({ open, editing, onClose }: Props) {
  const qc = useQueryClient();
  const toast = useToast();
  const fe = useFieldErrors<'vendorId'>();
  const [form, setForm] = useState<FormState>(emptyForm);

  useEffect(() => {
    if (!open) return;
    setForm(editing ? fromPo(editing) : emptyForm());
  }, [open, editing]);

  const { data: vendorsResp } = useQuery({
    queryKey: ['parties', { type: 'vendor', pageSize: 10000 }],
    queryFn: () => listParties({ type: 'vendor', pageSize: 10000 }),
    enabled: open,
    staleTime: 60_000,
  });
  const vendors: Party[] = useMemo(() => vendorsResp?.data ?? [], [vendorsResp]);

  const { data: itemsResp } = useQuery({
    queryKey: ['items', { pageSize: 10000, status: 'Active' }],
    queryFn: () => listItems({ pageSize: 10000, status: 'Active' }),
    enabled: open,
    staleTime: 5 * 60_000,
  });
  const itemMaster = useMemo(() => itemsResp?.data ?? [], [itemsResp]);

  const editingId = editing?.id ?? null;
  const isReadOnly = editing && editing.status !== 'Draft';

  // Compute totals locally.
  const totals = useMemo(() => {
    let subtotal = 0;
    let gstAmount = 0;
    for (const it of form.items) {
      const q = Number(it.qty || 0);
      const p = Number(it.unitPrice || 0);
      const g = Number(it.gstPct || 0);
      const line = q * p;
      subtotal += line;
      gstAmount += (line * g) / 100;
    }
    return {
      subtotal: Math.round(subtotal * 100) / 100,
      gstAmount: Math.round(gstAmount * 100) / 100,
      grandTotal: Math.round((subtotal + gstAmount) * 100) / 100,
    };
  }, [form.items]);

  const saveMutation = useMutation({
    mutationFn: () => {
      const payload = toPayload(form);
      if (editingId) {
        return updatePo(editingId, payload);
      }
      return createPo(payload);
    },
    onSuccess: (po) => {
      qc.invalidateQueries({ queryKey: ['po'] });
      if (editingId) {
        toast.push(`PO ${po.poNo} updated`, 'success');
      } else {
        toast.pushStep(`PO ${po.poNo} created`, {
          body: 'Next step — send to vendor for acknowledgement.',
          href: '/purchase/po',
          nextLabel: 'Open PO list',
          tag: `po-created-${po.id}`,
        });
      }
      onClose();
    },
    onError: (e: unknown) => {
      toast.push(apiError(e, 'Save failed'), 'error');
    },
  });

  function onSubmit() {
    if (!fe.validate({
      vendorId: !form.vendorId && 'Select a vendor',
    })) return;
    const valid = form.items.filter(
      (it) => it.itemName.trim() && Number(it.qty) > 0 && it.unit.trim() && Number(it.unitPrice) > 0,
    );
    if (valid.length === 0) {
      toast.push('Add at least one valid item with qty + unit + price', 'error');
      return;
    }
    saveMutation.mutate();
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editingId ? `Edit PO — ${editing?.poNo}` : 'New Purchase Order'}
      size="xl"
      footer={
        <>
          <Button variant="glass" onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            onClick={onSubmit}
            disabled={saveMutation.isPending || !!isReadOnly}
          >
            {saveMutation.isPending ? 'Saving…' : (<span className="inline-flex items-center gap-1"><Save size={14} /> Save PO</span>)}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-3">
        <FormGroup label="PO Number">
          <Input value={editing?.poNo ?? 'Auto'} readOnly className="opacity-70" />
        </FormGroup>
        <FormGroup label="Date" required>
          <Input
            type="date"
            value={form.poDate}
            onChange={(e) => setForm({ ...form, poDate: e.target.value })}
            disabled={!!isReadOnly}
          />
        </FormGroup>
        <FormGroup label="Subject" className="col-span-2">
          <Input
            value={form.subject}
            onChange={(e) => setForm({ ...form, subject: e.target.value })}
            placeholder="Short summary — carried to the GRN (optional)"
            maxLength={200}
            disabled={!!isReadOnly}
          />
        </FormGroup>
        <FormGroup label="Vendor" required error={fe.errors.vendorId}>
          <Select
            invalid={fe.has('vendorId')}
            value={String(form.vendorId)}
            onChange={(e) => { setForm({ ...form, vendorId: e.target.value ? Number(e.target.value) : '' }); fe.clear('vendorId'); }}
            disabled={!!isReadOnly}
          >
            <option value="">— Select Vendor —</option>
            {vendors.map((v) => (
              <option key={v.id} value={v.id}>
                {v.name} {v.city ? `· ${v.city}` : ''}
              </option>
            ))}
          </Select>
        </FormGroup>
        <FormGroup label="Delivery Date">
          <Input
            type="date"
            value={form.deliveryDate}
            onChange={(e) => setForm({ ...form, deliveryDate: e.target.value })}
            disabled={!!isReadOnly}
          />
        </FormGroup>
        <FormGroup label="Payment Terms (Days)">
          <Input
            type="number"
            value={form.paymentTermDays}
            onChange={(e) => setForm({ ...form, paymentTermDays: e.target.value })}
            disabled={!!isReadOnly}
          />
        </FormGroup>
        <FormGroup label="Delivery Address" className="col-span-1">
          <Input
            value={form.deliveryAddress}
            onChange={(e) => setForm({ ...form, deliveryAddress: e.target.value })}
            placeholder="Defaults to company address"
            disabled={!!isReadOnly}
          />
        </FormGroup>
        <FormGroup label="Terms & Conditions" className="col-span-2">
          <Textarea
            rows={2}
            value={form.termsConditions}
            onChange={(e) => setForm({ ...form, termsConditions: e.target.value })}
            disabled={!!isReadOnly}
          />
        </FormGroup>
      </div>

      <ItemsTable<PoItem>
        title="PO Items"
        rows={form.items}
        onChange={(items) => setForm({ ...form, items })}
        newRow={() => ({ itemName: '', hsn: '', qty: '', unit: '', unitPrice: '', gstPct: '12' })}
        columns={[
          {
            key: 'image',
            header: 'Image',
            width: '52px',
            cell: (row) => {
              const img = row.itemId != null ? (itemMaster.find((i) => i.id === row.itemId)?.imageUrl ?? null) : null;
              return <ItemThumb src={img} size={34} />;
            },
          },
          {
            key: 'item',
            header: 'Item',
            cell: (row, _i, update) => (
              <CategoryItemPicker
                showImage
                showSelectedThumb={false}
                showBarcode={false}
                items={itemMaster.map((it) => ({
                  id: it.id,
                  code: it.code,
                  name: it.name,
                  category: it.category,
                  currentStock: 0,
                  imageUrl: it.imageUrl,
                  barcode: it.barcode,
                }))}
                value={row.itemId ?? '__custom__'}
                disabled={!!isReadOnly}
                onCustom={() => update({ itemId: null })}
                onPick={(id) => {
                  const it = itemMaster.find((i) => i.id === id);
                  update({
                    itemId: id,
                    itemName: it?.name ?? row.itemName,
                    unit: it?.unit ?? row.unit,
                    hsn: it?.hsn ?? row.hsn,
                    gstPct: it?.gstPct ?? row.gstPct,
                    unitPrice: it ? Number(it.purchaseRate || 0) : row.unitPrice,
                  });
                }}
              />
            ),
          },
          {
            key: 'barcode',
            header: 'Barcode',
            width: '110px',
            cell: (row) => {
              const bc = row.itemId != null ? (itemMaster.find((i) => i.id === row.itemId)?.barcode ?? null) : null;
              return <span className="font-mono text-[11px] font-bold text-[var(--text-primary)]">{bc ?? '—'}</span>;
            },
          },
          {
            key: 'itemName',
            header: 'Description',
            cell: (row, _i, update) => (
              <Input
                value={row.itemName}
                onChange={(e) => update({ itemName: e.target.value })}
                disabled={!!isReadOnly}
              />
            ),
          },
          {
            key: 'hsn',
            header: 'HSN',
            width: '90px',
            cell: (row, _i, update) => (
              <Input
                value={String(row.hsn ?? '')}
                onChange={(e) => update({ hsn: e.target.value })}
                disabled={!!isReadOnly}
              />
            ),
          },
          {
            key: 'qty',
            header: 'Qty',
            width: '90px',
            align: 'right',
            cell: (row, _i, update) => (
              <Input
                type="number"
                value={String(row.qty ?? '')}
                onChange={(e) => update({ qty: e.target.value })}
                disabled={!!isReadOnly}
                className="text-right"
              />
            ),
          },
          {
            key: 'unit',
            header: 'Unit',
            width: '90px',
            cell: (row, _i, update) => (
              <Input
                value={row.unit}
                onChange={(e) => update({ unit: e.target.value })}
                disabled={!!isReadOnly}
              />
            ),
          },
          {
            key: 'unitPrice',
            header: 'Unit Price',
            width: '120px',
            align: 'right',
            cell: (row, _i, update) => (
              <Input
                type="number"
                step="0.01"
                value={String(row.unitPrice ?? '')}
                onChange={(e) => update({ unitPrice: Number(e.target.value) })}
                disabled={!!isReadOnly}
                className="text-right"
              />
            ),
          },
          {
            key: 'gstPct',
            header: 'GST%',
            width: '80px',
            align: 'right',
            cell: (row, _i, update) => (
              <Input
                type="number"
                value={String(row.gstPct ?? '')}
                onChange={(e) => update({ gstPct: e.target.value })}
                disabled={!!isReadOnly}
                className="text-right"
              />
            ),
          },
          {
            key: 'rowTotal',
            header: 'Total',
            width: '120px',
            align: 'right',
            cell: (row) => (
              <span className="font-bold text-accent">
                ₹{formatINR(Number(row.qty || 0) * Number(row.unitPrice || 0))}
              </span>
            ),
          },
        ]}
      />

      <div className="mt-3 flex justify-end">
        <div className="rounded-card border border-[var(--border-color)] bg-white/[0.03] p-3 text-sm">
          <div className="flex justify-between gap-6">
            <span className="text-[var(--text-secondary)]">Subtotal</span>
            <span className="font-bold">₹{formatINR(totals.subtotal)}</span>
          </div>
          <div className="flex justify-between gap-6">
            <span className="text-[var(--text-secondary)]">GST</span>
            <span className="font-bold">₹{formatINR(totals.gstAmount)}</span>
          </div>
          <div className="mt-1 flex justify-between gap-6 border-t border-[var(--border-color)] pt-1.5 text-base">
            <span className="font-extrabold text-[var(--text-primary)]">Grand Total</span>
            <span className="font-black text-accent">₹{formatINR(totals.grandTotal)}</span>
          </div>
        </div>
      </div>
    </Modal>
  );
}

function toPayload(f: FormState) {
  return {
    poDate: f.poDate,
    subject: f.subject.trim(),
    vendorId: f.vendorId as number,
    deliveryDate: f.deliveryDate || undefined,
    paymentTermDays: f.paymentTermDays ? Number(f.paymentTermDays) : undefined,
    deliveryAddress: f.deliveryAddress.trim() || undefined,
    termsConditions: f.termsConditions.trim() || undefined,
    items: f.items
      .filter((it) => it.itemName.trim() && Number(it.qty) > 0 && it.unit.trim() && Number(it.unitPrice) > 0)
      .map((it) => ({
        itemId: it.itemId ?? undefined,
        itemName: it.itemName.trim(),
        hsn: (it.hsn ?? '').toString().trim() || undefined,
        qty: Number(it.qty),
        unit: it.unit.trim(),
        unitPrice: Number(it.unitPrice),
        gstPct: it.gstPct != null && it.gstPct !== '' ? Number(it.gstPct) : 0,
      })),
  };
}
